import React from 'react';
import { useAudio } from '../../contexts/AudioContext';
import { Music, Radio, User } from 'lucide-react';

export function NowPlaying({ compact = false }) {
  const { stationData, isPlaying } = useAudio();
  const nowPlaying = stationData?.now_playing;
  const song = nowPlaying?.song;
  const live = stationData?.live;

  if (!song) {
    return (
      <div className="animate-pulse" data-testid="now-playing-skeleton">
        <div className="h-8 bg-surface-highlight rounded w-3/4 mb-2" />
        <div className="h-6 bg-surface-highlight rounded w-1/2" />
      </div>
    );
  }

  if (compact) {
    return (
      <div className="flex items-center gap-3" data-testid="now-playing-compact">
        <div className={`w-2 h-2 rounded-full ${isPlaying ? 'bg-green-500 animate-pulse' : 'bg-zinc-500'}`} />
        <div className="overflow-hidden">
          <p className="text-sm font-medium truncate" style={{ color: 'var(--text-main)' }}>
            {song.title}
          </p>
          <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
            {song.artist}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="now-playing-full">
      {/* Live indicator */}
      {live?.is_live && (
        <div className="flex items-center gap-2 text-red-500 mb-2">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
          </span>
          <span className="text-sm font-medium">LIVE</span>
          {live.streamer_name && (
            <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
              with {live.streamer_name}
            </span>
          )}
        </div>
      )}

      {/* Playlist badge */}
      {nowPlaying?.playlist && (
        <div 
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium"
          style={{ 
            backgroundColor: 'var(--surface-highlight)',
            color: 'var(--primary)'
          }}
          data-testid="playlist-badge"
        >
          <Radio className="w-3 h-3" />
          {nowPlaying.playlist}
        </div>
      )}

      {/* Song info */}
      <div className="space-y-2">
        <h1 
          className="text-2xl md:text-3xl font-bold tracking-tight leading-tight truncate"
          style={{ 
            fontFamily: 'Teko, sans-serif',
            color: 'var(--text-main)'
          }}
          data-testid="song-title"
          title={song.title}
        >
          {song.title}
        </h1>
        
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--primary)' }} />
          <p 
            className="text-lg md:text-xl font-medium truncate"
            style={{ color: 'var(--text-muted)' }}
            data-testid="song-artist"
            title={song.artist}
          >
            {song.artist}
          </p>
        </div>
        
        {song.album && (
          <div className="flex items-center gap-2">
            <Music className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--text-muted)' }} />
            <p 
              className="text-sm truncate"
              style={{ color: 'var(--text-muted)' }}
              data-testid="song-album"
              title={song.album}
            >
              {song.album}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
